"use client";
import { useState } from "react";
import Link from "next/link";
import { useTypewriter } from "@/hooks/animations/useTypewriter";

interface ViewCaseStudyLinkProps {
  href: string;
  label?: string;
}

// The "View Case Study" link. The label types itself out, holds, erases and
// retypes forever, and the underline lives *under the typed text only* — so it
// grows and shrinks in lockstep with the letters rather than sitting at a fixed
// width. Hovering stops the loop and settles on the full label + full underline.
// An invisible copy reserves the final width so the row never reflows.
const ViewCaseStudyLink = ({
  href,
  label = "View Case Study",
}: ViewCaseStudyLinkProps) => {
  const [hovered, setHovered] = useState(false);
  const shown = useTypewriter(label, !hovered, { loop: true, startDelay: 150 });
  const count = hovered ? label.length : shown;
  const typed = label.slice(0, count);

  return (
    <Link
      href={href}
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative inline-block text-sm font-bold text-card-foreground lg:text-base"
    >
      <span className="invisible" aria-hidden>
        {label}
      </span>
      <span className="absolute inset-0 whitespace-nowrap">
        {/* Border on the typed slice, so its width == the visible text width. */}
        <span className="border-b-2 border-card-foreground pb-0.5">{typed}</span>
        {!hovered && (
          <span className="ml-0.5 inline-block h-[0.9em] w-0.5 translate-y-[0.1em] animate-pulse bg-card-foreground align-middle" />
        )}
      </span>
    </Link>
  );
};

export default ViewCaseStudyLink;
